/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, June 2020
 */

import React, { useRef, useState } from 'react';
import { useFormContext, ValidationOptions } from 'react-hook-form';
import { FlowBlockInput as BlockModel } from '@staffino/model';
import { useTranslation } from 'react-i18next';
import { ScrollAnchor, useScrollEffect } from './scroll';
import isEnterPressed from './input-submit';
import { TextInputControl } from './controls';

function getValidation(type: string): ValidationOptions {
  switch (type) {
    case 'Email':
      return {
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: 'Please check and correct the email address',
        },
      };
    case 'Phone':
      return {
        pattern: {
          value: /^[\+|00][0-9]{1}[0-9\ \/-]*[0-9]{1}$/im,
          message: 'Please check and correct the phone number',
        },
      };
    case 'Number':
      return {
        pattern: {
          value: /^[0-9]*$/im,
          message: 'Only numbers are allowed',
        },
      };
    default:
      return null;
  }
}

function calculatePlaceholder(inputType, t) {
  switch (inputType) {
    case 'Email':
      return t('Email');
    case 'Phone':
      return t('Phone');
    case 'Number':
      return t('Number');
    case 'String':
      return t('String');
    default:
      return t('Label name');
  }
}

function calculateMaxWidth(inputType) {
  switch (inputType) {
    case 'Number':
      return '15rem';
    default:
      return '30rem';
  }
}

type Props = {
  block: BlockModel;
};

export default function TextInputBlock({ block }: Props) {
  const context = useFormContext();
  const inputRef = useRef();
  const error = context?.errors?.[block.id];

  const { t } = useTranslation();
  const validation: ValidationOptions = {
    required: block.required,
    ...getValidation(block.inputType),
  };
  const label = block.label || calculatePlaceholder(block.inputType, t);

  const [enterTriggered, setEnterTriggered] = useState(false);
  const { scrollToError } = useScrollEffect(
    block.id,
    enterTriggered && !error ? 'validValue' : null,
  );

  return (
    <>
      <ScrollAnchor id={block.id} focusOn={inputRef} />
      <TextInputControl
        blockId={block.id}
        name={block.id}
        ref={(element: any) => {
          context?.register?.(element, validation);
          inputRef.current = element;
        }}
        label={label}
        labelShowMandatory={block.required && !!label}
        tooltip={{
          content: t('This field is required.'),
          ...(block.required && {
            showOn: label ? 'label' : 'input',
          }),
        }}
        defaultValue={block.inputType === 'Email' ? '' : block.defaultValue}
        placeholder={`${block.placeholder}${
          block.required && !label ? ' *' : ''
        }`}
        limit={block.limit}
        activeColor="primary"
        width={1}
        maxWidth={calculateMaxWidth(block.inputType)}
        m="1rem auto"
        invalid={!!error}
        requiredValidationMessage={
          error?.type === 'required' && t('Please fill out this field')
        }
        validationMessage={error?.message}
        onKeyDown={(event) => setEnterTriggered(isEnterPressed(event))}
        onFocus={() => scrollToError(block.id)}
      />
    </>
  );
}
